// PreloadManager.js - Smart progressive preloading for audio and video clips
import { AudioStore } from '@/front/stores/audio_store.js'
import { ClipStore } from '@/front/stores/clip_store.js'
import { AnnStore } from '@/front/stores/ann_store.js'
import axios from 'axios'

class PreloadManager {
  constructor() {
    this.preloadedClips = new Map() // id -> { url, element, type, timestamp }
    this.preloadQueue = []
    this.isPreloading = false
    this.maxPreloads = 5 // Maximum number of clips to keep preloaded
    this.preloadWindow = 60 // Seconds ahead to preload
    this.cleanupThreshold = 10 // Number of preloads before cleanup
    this.preloadAttempts = new Map() // Track failed attempts
  }

  // Initialize preloading for a video
  init(annotations, currentTime = 0) {
    log.info(3, "🎬 PreloadManager init", annotations.length, currentTime)
    this.reset()
    this.buildPreloadQueue(annotations, currentTime)
    this.startPreloading()
  }

  // Build priority queue based on timeline
  buildPreloadQueue(annotations, currentTime) {
    log.info(2, "🎬 PreloadManager buildPreloadQueue", annotations.length, currentTime)
    const clipsToPreload = []

    annotations.forEach(ann => {
      // Check for direct video/audio interactions
      if (ann.type_of === 'video' && ann.content) {
        clipsToPreload.push({
          id: ann.content,
          type: 'video',
          time: ann.exact_time,
          priority: this.calculatePriority(ann.exact_time, currentTime)
        })
      } else if (ann.type_of === 'audio' && ann.content) {
        clipsToPreload.push({
          id: ann.content,
          type: 'audio',
          time: ann.exact_time,
          priority: this.calculatePriority(ann.exact_time, currentTime)
        })
      }

      // Check click actions
      if (ann.click_action === 'clip' && ann.click_value) {
        clipsToPreload.push({
          id: ann.click_value,
          type: 'video',
          time: ann.exact_time,
          priority: this.calculatePriority(ann.exact_time, currentTime)
        })
      } else if (ann.click_action === 'audio' && ann.click_value) {
        clipsToPreload.push({
          id: ann.click_value,
          type: 'audio',
          time: ann.exact_time,
          priority: this.calculatePriority(ann.exact_time, currentTime)
        })
      }

      // Check question answers
      if (ann.question_answers && Array.isArray(ann.question_answers)) {
        ann.question_answers.forEach(answer => {
          if (answer.click_action === 'clip' && answer.click_value) {
            clipsToPreload.push({
              id: answer.click_value,
              type: 'video',
              time: ann.exact_time,
              priority: this.calculatePriority(ann.exact_time, currentTime)
            })
          } else if (answer.click_action === 'audio' && answer.click_value) {
            clipsToPreload.push({
              id: answer.click_value,
              type: 'audio',
              time: ann.exact_time,
              priority: this.calculatePriority(ann.exact_time, currentTime)
            })
          }
        })
      }
    })

    // Sort by priority and deduplicate
    this.preloadQueue = clipsToPreload
      .filter((clip, index, self) =>
        index === self.findIndex(c => c.id === clip.id)
      )
      .sort((a, b) => b.priority - a.priority)

    log.info(2, "🎬 PreloadManager queue built", this.preloadQueue.length)
  }

  // Calculate priority based on timeline proximity
  calculatePriority(clipTime, currentTime) {
    const timeDiff = clipTime - currentTime

    // Already passed
    if (timeDiff < 0) return -1

    // Within next 10 seconds - highest priority
    if (timeDiff <= 10) return 100

    // Within next 30 seconds - high priority
    if (timeDiff <= 30) return 75

    // Within preload window - medium priority
    if (timeDiff <= this.preloadWindow) return 50

    // Beyond window - low priority
    return 25
  }

  // Start preloading process
  async startPreloading() {
    log.info(2, "🎬 PreloadManager startPreloading")
    if (this.isPreloading || this.preloadQueue.length === 0) return

    this.isPreloading = true

    while (this.preloadQueue.length > 0 && this.preloadedClips.size < this.maxPreloads) {
      const clip = this.preloadQueue.shift()

      // Skip if already preloaded or priority too low
      if (this.preloadedClips.has(clip.id) || clip.priority < 0) continue

      // Skip if failed too many times
      const attempts = this.preloadAttempts.get(clip.id) || 0
      if (attempts >= 3) continue

      await this.preloadClip(clip)

      // Small delay between preloads to avoid overwhelming the browser
      await new Promise(resolve => setTimeout(resolve, 100))
    }

    this.isPreloading = false

  }

  // Preload a single clip
  async preloadClip(clip) {
    try {
      log.info(2, "🎬 PreloadManager preloading", clip.type, clip.id)

      // Fetch the streaming URL
      const response = await axios.get(`/assets/stream/${clip.id}`)
      const url = response.data.url

      if (!url) {
        throw new Error("No URL received")
      }

      // Create preload element
      let element
      if (clip.type === 'video') {
        element = document.createElement('video')
        // Metadata-only preload avoids large MP4 downloads before user intent.
        element.preload = 'metadata'
        element.muted = true // Required for autoplay policies
      } else {
        element = document.createElement('audio')
        element.preload = 'auto'
      }

      // Set source and start loading
      element.src = url
      element.load()
      element.style.display = 'none'
      document.body.appendChild(element)

      // Wait for metadata to load
      await new Promise((resolve, reject) => {
        element.addEventListener('loadedmetadata', resolve, { once: true })
        element.addEventListener('error', reject, { once: true })
        setTimeout(reject, 10000) // 10 second timeout
      })

      // Store preload info
      this.preloadedClips.set(clip.id, {
        url,
        element,
        type: clip.type,
        timestamp: Date.now(),
        time: clip.time
      })

      log.info(2, "✅ PreloadManager preloaded", clip.type, clip.id)

    } catch (error) {
      log.info(1, "❌ PreloadManager failed to preload", clip.id, error)
      const attempts = this.preloadAttempts.get(clip.id) || 0
      this.preloadAttempts.set(clip.id, attempts + 1)
    }
  }

  // Get preloaded clip if available
  getPreloadedClip(id) {
    const preload = this.preloadedClips.get(id)
    if (preload) {
      log.info(2, "🎯 PreloadManager cache hit", id)
      // Update timestamp for LRU
      preload.timestamp = Date.now()
      return preload
    }
    log.info(2, "💤 PreloadManager cache miss", id)
    return null
  }

  // Transfer ownership of preloaded element to player
  takePreloadedElement(id) {
    const preload = this.preloadedClips.get(id)
    if (preload && preload.element) {
      log.info(2, "🎯 PreloadManager transferring element ownership", id)
      // Remove from preload tracking since player now owns it
      this.preloadedClips.delete(id)
      return preload.element
    }
    return null
  }

  // Reset all preloads
  reset() {
    log.info(2, "🔄 PreloadManager reset")
    this.preloadedClips.forEach(preload => {
      if (preload.element && preload.element.parentNode) {
        preload.element.parentNode.removeChild(preload.element)
      }
    })
    this.preloadedClips.clear()
    this.preloadQueue = []
    this.preloadAttempts.clear()
    this.isPreloading = false
  }

  // Preload specific clip immediately (for hover or likely interaction)
  preloadNow(id, type) {
    if (this.preloadedClips.has(id)) return

    const clip = { id, type, time: 0, priority: 100 }
    this.preloadClip(clip)
  }
}

// Singleton instance
export const preloadManager = new PreloadManager()
